/**
 * Field transformation utilities for converting between snake_case (backend) and camelCase (frontend)
 * Handles nested objects, arrays, and preserves null/undefined values
 */

/**
 * Convert snake_case string to camelCase
 * @param str - The snake_case string to convert
 * @returns The camelCase version of the string
 */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Convert camelCase string to snake_case
 * @param str - The camelCase string to convert
 * @returns The snake_case version of the string
 */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Generic type for transformation direction
 */
export type TransformDirection = 'toFrontend' | 'toBackend';

/**
 * Transform function type
 */
type KeyTransformer = (key: string) => string;

/**
 * Recursively transform all keys in an object or array
 * @param data - The data to transform (object, array, or primitive)
 * @param transformer - The key transformation function
 * @returns The transformed data with converted keys
 */
export function transformKeys<T = unknown>(
  data: unknown,
  transformer: KeyTransformer
): T {
  // Handle null and undefined
  if (data === null || data === undefined) {
    return data as T;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map(item => transformKeys(item, transformer)) as T;
  }

  // Handle objects (but not dates or other built-in objects)
  if (typeof data === 'object' && !(data instanceof Date)) {
    const transformed: Record<string, unknown> = {};
    
    for (const [key, value] of Object.entries(data)) {
      const newKey = transformer(key);
      transformed[newKey] = transformKeys(value, transformer);
    }
    
    return transformed as T;
  }

  // Return primitives as-is
  return data as T;
}

/**
 * Transform data from backend (snake_case) to frontend (camelCase)
 * @param data - The backend data to transform
 * @returns The transformed data with camelCase keys
 */
export function transformToFrontend<T = unknown>(data: unknown): T {
  return transformKeys<T>(data, snakeToCamel);
}

/**
 * Transform data from frontend (camelCase) to backend (snake_case)
 * @param data - The frontend data to transform
 * @returns The transformed data with snake_case keys
 */
export function transformToBackend<T = unknown>(data: unknown): T {
  return transformKeys<T>(data, camelToSnake);
}

/**
 * Invoice-specific transformers with type safety
 * Transforms between billing_InvoiceDTO (snake_case from backend) and Invoice (camelCase for frontend)
 */
export const InvoiceTransformer = {
  /**
   * Transform invoice data from backend (snake_case) to frontend (camelCase) format
   * @param invoice - Raw invoice DTO from the API with snake_case fields
   * @returns Transformed invoice with camelCase fields
   */
  toFrontend(invoice: unknown): unknown {
    const transformed = transformToFrontend(invoice);

    // Handle specific invoice fields
    if (transformed && typeof transformed === 'object') {
      const inv = transformed as Record<string, unknown>;

      // The list endpoint returns `billing_year/month` while the single-invoice
      // endpoint returns `billing_period_year/month`. Normalize both shapes to
      // `billingYear/billingMonth` so consumers (InvoiceCard, etc.) don't have
      // to branch. Consumers expect numbers and choke on undefined (e.g.
      // `new Date(undefined, NaN)` throws "Invalid time value").
      if (inv.billingYear === undefined && typeof inv.billingPeriodYear === 'number') {
        inv.billingYear = inv.billingPeriodYear;
      }
      if (inv.billingMonth === undefined && typeof inv.billingPeriodMonth === 'number') {
        inv.billingMonth = inv.billingPeriodMonth;
      }

      // Convert date strings to Date objects
      if (inv.dueDate && typeof inv.dueDate === 'string') {
        inv.dueDate = new Date(inv.dueDate);
      }
      if (inv.invoiceDate && typeof inv.invoiceDate === 'string') {
        inv.invoiceDate = new Date(inv.invoiceDate);
      }
      if (inv.periodStart && typeof inv.periodStart === 'string') {
        inv.periodStart = new Date(inv.periodStart);
      }
      if (inv.periodEnd && typeof inv.periodEnd === 'string') {
        inv.periodEnd = new Date(inv.periodEnd);
      }
    }

    return transformed;
  },

  /**
   * Transform invoice data from frontend (camelCase) to backend (snake_case) format
   * @param invoice - Frontend invoice with camelCase fields
   * @returns Transformed invoice with snake_case fields for API
   */
  toBackend(invoice: unknown): unknown {
    const transformed = transformToBackend(invoice);

    // Handle specific invoice fields
    if (transformed && typeof transformed === 'object') {
      const inv = transformed as Record<string, unknown>;

      // Convert Date objects to ISO strings
      if (inv.due_date instanceof Date) {
        inv.due_date = inv.due_date.toISOString();
      }
      if (inv.invoice_date instanceof Date) {
        inv.invoice_date = inv.invoice_date.toISOString();
      }
      if (inv.period_start instanceof Date) {
        inv.period_start = inv.period_start.toISOString();
      }
      if (inv.period_end instanceof Date) {
        inv.period_end = inv.period_end.toISOString();
      }
    }

    return transformed;
  },

  /**
   * Transform array of invoices from backend to frontend format
   * @param invoices - Array of raw invoice DTOs from the API
   * @returns Array of transformed invoices with camelCase fields
   */
  arrayToFrontend<TInput = unknown, TOutput = unknown>(invoices: TInput[]): TOutput[] {
    return invoices.map(invoice => InvoiceTransformer.toFrontend(invoice) as unknown as TOutput);
  },

  /**
   * Transform array of invoices from frontend to backend format
   * @param invoices - Array of frontend invoices with camelCase fields
   * @returns Array of transformed invoices with snake_case fields
   */
  arrayToBackend<TInput = unknown, TOutput = unknown>(invoices: TInput[] | null | undefined): TOutput[] {
    if (!invoices) {
      return [];
    }
    return invoices
      .map(invoice => InvoiceTransformer.toBackend(invoice) as unknown as TOutput | undefined)
      .filter((invoice): invoice is TOutput => invoice !== undefined);
  }
};

/**
 * Contract-specific transformers with type safety
 */
export const ContractTransformer = {
  /**
   * Transform contract data from backend to frontend format
   */
  toFrontend<T = unknown>(contract: unknown): T {
    const transformed = transformToFrontend<T>(contract);
    
    // Handle specific contract fields
    if (transformed && typeof transformed === 'object') {
      const ctr = transformed as Record<string, unknown>;
      
      // Convert date strings to Date objects
      if (ctr.startDate && typeof ctr.startDate === 'string') {
        ctr.startDate = new Date(ctr.startDate);
      }
      if (ctr.endDate && typeof ctr.endDate === 'string') {
        ctr.endDate = new Date(ctr.endDate);
      }
      if (ctr.terminatedAt && typeof ctr.terminatedAt === 'string') {
        ctr.terminatedAt = new Date(ctr.terminatedAt);
      }
      
      // Transform nested service pattern if present
      if (ctr.servicePattern && typeof ctr.servicePattern === 'object') {
        ctr.servicePattern = transformToFrontend(ctr.servicePattern);
      }
      
      // Transform nested payers array if present
      if (Array.isArray(ctr.payers)) {
        ctr.payers = ctr.payers.map(payer => {
          const transformed = transformToFrontend(payer) as Record<string, unknown>;
          // Remove deprecated isPrimary field if present
          if ('isPrimary' in transformed) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { isPrimary, ...rest } = transformed;
            return rest;
          }
          return transformed;
        });
      }
    }
    
    return transformed;
  },

  /**
   * Transform contract data from frontend to backend format
   */
  toBackend<T = unknown>(contract: unknown): T {
    const transformed = transformToBackend<T>(contract);
    
    // Handle specific contract fields
    if (transformed && typeof transformed === 'object') {
      const ctr = transformed as Record<string, unknown>;
      
      // Convert Date objects to ISO strings
      if (ctr.start_date instanceof Date) {
        ctr.start_date = ctr.start_date.toISOString();
      }
      if (ctr.end_date instanceof Date) {
        ctr.end_date = ctr.end_date.toISOString();
      }
      if (ctr.terminated_at instanceof Date) {
        ctr.terminated_at = ctr.terminated_at.toISOString();
      }
      
      // Transform nested service pattern if present
      if (ctr.service_pattern && typeof ctr.service_pattern === 'object') {
        ctr.service_pattern = transformToBackend(ctr.service_pattern);
      }
      
      // Transform nested payers array if present
      if (Array.isArray(ctr.payers)) {
        ctr.payers = ctr.payers.map(payer => transformToBackend(payer));
      }
    }
    
    return transformed;
  },

  /**
   * Transform array of contracts
   */
  arrayToFrontend<T = unknown>(contracts: unknown[]): T[] {
    return contracts.map(contract => ContractTransformer.toFrontend<T>(contract));
  },

  /**
   * Transform array of contracts to backend format
   */
  arrayToBackend<T = unknown>(contracts: unknown[]): T[] {
    return contracts.map(contract => ContractTransformer.toBackend<T>(contract));
  }
};

/**
 * Tariff-specific transformers with type safety
 */
export const TariffTransformer = {
  /**
   * Transform tariff data from backend to frontend format
   */
  toFrontend<T = unknown>(tariff: unknown): T {
    const transformed = transformToFrontend<T>(tariff);
    
    // Handle specific tariff fields
    if (transformed && typeof transformed === 'object') {
      const trf = transformed as Record<string, unknown>;
      
      // Convert date strings to Date objects
      if (trf.validFrom && typeof trf.validFrom === 'string') {
        trf.validFrom = new Date(trf.validFrom);
      }
      if (trf.validTo && typeof trf.validTo === 'string') {
        trf.validTo = new Date(trf.validTo);
      }
      
      // Transform nested rates array if present
      if (Array.isArray(trf.rates)) {
        trf.rates = trf.rates.map(rate => transformToFrontend(rate));
      }
    }
    
    return transformed;
  },

  /**
   * Transform tariff data from frontend to backend format
   */
  toBackend<T = unknown>(tariff: unknown): T {
    const transformed = transformToBackend<T>(tariff);
    
    // Handle specific tariff fields
    if (transformed && typeof transformed === 'object') {
      const trf = transformed as Record<string, unknown>;
      
      // Convert Date objects to ISO strings
      if (trf.valid_from instanceof Date) {
        trf.valid_from = trf.valid_from.toISOString();
      }
      if (trf.valid_to instanceof Date) {
        trf.valid_to = trf.valid_to.toISOString();
      }
      
      // Transform nested rates array if present
      if (Array.isArray(trf.rates)) {
        trf.rates = trf.rates.map(rate => transformToBackend(rate));
      }
    }
    
    return transformed;
  },

  /**
   * Transform array of tariffs
   */
  arrayToFrontend<T = unknown>(tariffs: unknown[]): T[] {
    return tariffs.map(tariff => TariffTransformer.toFrontend<T>(tariff));
  },

  /**
   * Transform array of tariffs to backend format
   */
  arrayToBackend<T = unknown>(tariffs: unknown[]): T[] {
    return tariffs.map(tariff => TariffTransformer.toBackend<T>(tariff));
  }
};

/**
 * Generic entity transformer for other entities
 */
export const EntityTransformer = {
  /**
   * Transform any entity from backend to frontend format
   */
  toFrontend<T = unknown>(entity: unknown): T {
    return transformToFrontend<T>(entity);
  },

  /**
   * Transform any entity from frontend to backend format
   */
  toBackend<T = unknown>(entity: unknown): T {
    return transformToBackend<T>(entity);
  },

  /**
   * Transform array of entities
   */
  arrayToFrontend<T = unknown>(entities: unknown[]): T[] {
    return entities.map(entity => EntityTransformer.toFrontend<T>(entity));
  },

  /**
   * Transform array of entities to backend format
   */
  arrayToBackend<T = unknown>(entities: unknown[]): T[] {
    return entities.map(entity => EntityTransformer.toBackend<T>(entity));
  }
};

/**
 * Batch transformation utilities for performance
 */
export const BatchTransformer = {
  /**
   * Transform multiple different entity types in one operation
   */
  toFrontend(data: {
    invoices?: unknown[];
    contracts?: unknown[];
    tariffs?: unknown[];
    [key: string]: unknown;
  }): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    
    if (data.invoices) {
      result.invoices = InvoiceTransformer.arrayToFrontend(data.invoices);
    }
    
    if (data.contracts) {
      result.contracts = ContractTransformer.arrayToFrontend(data.contracts);
    }
    
    if (data.tariffs) {
      result.tariffs = TariffTransformer.arrayToFrontend(data.tariffs);
    }
    
    // Handle any other properties generically
    for (const [key, value] of Object.entries(data)) {
      if (!['invoices', 'contracts', 'tariffs'].includes(key)) {
        result[key] = transformToFrontend(value);
      }
    }
    
    return result;
  },

  /**
   * Transform multiple different entity types to backend format
   */
  toBackend(data: {
    invoices?: unknown[];
    contracts?: unknown[];
    tariffs?: unknown[];
    [key: string]: unknown;
  }): Record<string, unknown> {
    const result: Record<string, unknown> = {};
    
    if (data.invoices) {
      result.invoices = InvoiceTransformer.arrayToBackend(data.invoices);
    }
    
    if (data.contracts) {
      result.contracts = ContractTransformer.arrayToBackend(data.contracts);
    }
    
    if (data.tariffs) {
      result.tariffs = TariffTransformer.arrayToBackend(data.tariffs);
    }
    
    // Handle any other properties generically
    for (const [key, value] of Object.entries(data)) {
      if (!['invoices', 'contracts', 'tariffs'].includes(key)) {
        result[key] = transformToBackend(value);
      }
    }
    
    return result;
  }
};

/**
 * Utility to create a custom transformer for specific entity types
 */
export function createEntityTransformer<TFrontend = unknown, TBackend = unknown>(
  customToFrontend?: (data: TBackend) => TFrontend,
  customToBackend?: (data: TFrontend) => TBackend
) {
  return {
    toFrontend(data: TBackend): TFrontend {
      const transformed = transformToFrontend<TFrontend>(data);
      return customToFrontend ? customToFrontend(transformed as unknown as TBackend) : transformed;
    },
    
    toBackend(data: TFrontend): TBackend {
      const transformed = transformToBackend<TBackend>(data);
      return customToBackend ? customToBackend(transformed as unknown as TFrontend) : transformed;
    },
    
    arrayToFrontend(data: TBackend[]): TFrontend[] {
      return data.map(item => this.toFrontend(item));
    },
    
    arrayToBackend(data: TFrontend[]): TBackend[] {
      return data.map(item => this.toBackend(item));
    }
  };
}